import React from 'react'
import { TestItem, FilterType } from '../types/reports'

interface TestSectionProps {
  sectionTitle: string
  cssClass: string
  ariaLabel: string
  tests: TestItem[]
  expanded: boolean
  onToggle: () => void
  filter: FilterType
  onFilterChange: (filter: FilterType) => void
  onErrorClick: (messages: string[], testTitle: string) => void
}

const formatDuration = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(2)}s`
}

const getStatusClass = (status: string) => {
  if (status === 'passed') return 'test-status-chip passed'
  if (status === 'failed') return 'test-status-chip failed'
  if (status === 'flaky') return 'test-status-chip flaky'
  return 'test-status-chip'
}

const getStatusIcon = (status: string) => {
  if (status === 'passed') return 'Pass ✅'
  if (status === 'failed') return 'Fail ❌'
  if (status === 'flaky') return 'Flaky ⚠️'
  return ''
}

const TestSection: React.FC<TestSectionProps> = ({
  sectionTitle,
  cssClass,
  ariaLabel,
  tests,
  expanded,
  onToggle,
  filter,
  onFilterChange,
  onErrorClick,
}) => {
  if (tests.length === 0) return null

  const passedCount = tests.filter(t => t.status === 'passed').length
  const failedCount = tests.filter(t => t.status === 'failed').length
  const flakyCount = tests.filter(t => t.status === 'flaky').length
  const filteredTests = filter === 'all' ? tests : tests.filter(t => t.status === filter)

  return (
    <div className={cssClass}>
      <div className={`${cssClass}-header`} role="button" aria-label={ariaLabel} onClick={onToggle}>
        <div
          className={`expand-arrow ${expanded ? 'expanded' : ''}`}
          title={expanded ? `Collapse ${sectionTitle.toLowerCase()}` : `Expand ${sectionTitle.toLowerCase()}`}
        >
          ▶
        </div>
        <div className="section-title">
          <p>
            <strong>
              {sectionTitle} ({tests.length})
            </strong>
          </p>
          <div className="status-summary">
            {passedCount > 0 && <span className="status-count passed">✅ {passedCount}</span>}
            {failedCount > 0 && <span className="status-count failed">❌ {failedCount}</span>}
            {flakyCount > 0 && <span className="status-count flaky">⚠️ {flakyCount}</span>}
          </div>
        </div>
      </div>

      {expanded && (
        <div className={`${cssClass}-content expanded`}>
          <div className="test-filters">
            <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => onFilterChange('all')}>
              All
            </button>
            <button
              className={`filter-btn ${filter === 'passed' ? 'active' : ''}`}
              onClick={() => onFilterChange('passed')}
            >
              ✅ Pass
            </button>
            <button
              className={`filter-btn ${filter === 'failed' ? 'active' : ''}`}
              onClick={() => onFilterChange('failed')}
            >
              ❌ Fail
            </button>
            <button
              className={`filter-btn ${filter === 'flaky' ? 'active' : ''}`}
              onClick={() => onFilterChange('flaky')}
            >
              ⚠️ Flaky
            </button>
          </div>
          <div className="tests-inline">
            {filteredTests.map(test => (
              <div
                key={test.key}
                className={`test-card ${test.status === 'failed' ? 'failed-test-card' : ''}`}
                onClick={test.status === 'failed' ? () => onErrorClick(test.errors, test.title) : undefined}
                style={{ cursor: test.status === 'failed' ? 'pointer' : 'default' }}
              >
                <div className="test-header">
                  <h6 className="test-title">{test.title}</h6>
                  <p className="test-filename">{test.filename}</p>
                </div>
                <div className="test-details">
                  <span className={getStatusClass(test.status)}>{getStatusIcon(test.status)}</span>
                  <span className="test-duration">{formatDuration(test.duration)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default TestSection
